import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { initAnalytics, trackPageView } from './utils/analytics';
import { QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import { queryClient } from './lib/queryClient';
import { QuestionnaireProvider, AuthProvider, NotificationProvider, ThemeProvider, SubscriptionProvider, useAuth } from './context';
import { Sidebar } from './components/common/Navigation';
import EmailVerificationBanner from './components/common/EmailVerificationBanner';
import ProtectedRoute from './components/common/ProtectedRoute';
import RouteAnnouncer from './components/common/RouteAnnouncer';
import ScrollToTop from './components/common/ScrollToTop';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import PlanUnlockedModal from './components/common/PlanUnlockedModal/PlanUnlockedModal';
import SeoManager from './components/common/SeoManager';

import 'react-toastify/dist/ReactToastify.css';
import './styles/variables.css';
import './styles/globals.css';
import Bloom from './components/common/Bloom/Bloom';

// Auth pages loaded eagerly (no flash on login/register switch)

// Lazy load other pages for performance (code splitting)
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const AfterADeath = lazy(() => import('./pages/AfterADeath/AfterADeath'));
const RemembrancePage = lazy(() => import('./pages/CareTeamPage/Rites/RemembrancePage'));
const CareFundConfirm = lazy(() => import('./pages/CareFundConfirm'));
const MockCheckout = lazy(() => import('./pages/MockCheckout'));
const SponsorPage = lazy(() => import('./pages/SponsorPage'));
const SponsorLandingPage = lazy(() => import('./pages/SponsorLanding/SponsorLandingPage'));
const GetSponsoredPage = lazy(() => import('./pages/CareTeamPage/GetSponsoredPage'));
const SponsorFamiliesPage = lazy(() => import('./pages/SponsorLanding/SponsorFamiliesPage'));
const SponsorBulkPage = lazy(() => import('./pages/SponsorLanding/SponsorBulkPage'));
const SponsorCreditsPage = lazy(() => import('./pages/SponsorLanding/SponsorCreditsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const LegalCenter = lazy(() => import('./pages/LegalCenter/LegalCenter'));
const GiftClaimPage = lazy(() => import('./pages/GiftClaim/GiftClaimPage'));
const SponsorImpactPage = lazy(() => import('./pages/SponsorImpact/SponsorImpactPage'));
const PaymentComplete = lazy(() => import('./pages/PaymentComplete'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'));
const SSOCallback = lazy(() => import('./pages/SSOCallback'));
const VerificationPending = lazy(() => import('./pages/VerificationPending'));
const RestoreAccount = lazy(() => import('./pages/RestoreAccount'));
const AccountSettings = lazy(() => import('./pages/AccountSettings'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CareTeamPage = lazy(() => import('./pages/CareTeamPage'));
const TeamBillingPage = lazy(() => import('./pages/CareTeamPage/TeamBillingPage'));
const CareFundLayout = lazy(() => import('./pages/CareTeamPage/Funding/CareFundLayout'));
const CareFundPage = lazy(() => import('./pages/CareTeamPage/Funding/CareFundPage'));
const PoolDetailPage = lazy(() => import('./pages/CareTeamPage/Funding/PoolDetailPage'));
const FundingTeamPicker = lazy(() => import('./pages/Funding/FundingTeamPicker'));
const CareTeamsPage = lazy(() => import('./pages/CareTeamsPage'));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage'));
const MyRecordingsPage = lazy(() => import('./pages/MyRecordingsPage'));
const YourProgressPage = lazy(() => import('./pages/YourProgressPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const QuestionnaireFlow = lazy(() => import('./pages/QuestionnaireFlow'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const AdvocacyWorkbookHomeProd = lazy(() => import('./pages/Advocacy/WorkbookHome'));
const AdvocacyWorkbookTeamPickerProd = lazy(() => import('./pages/Advocacy/WorkbookTeamPicker'));
const AdvocacyStageDetailProd = lazy(() => import('./pages/Advocacy/StageDetail'));
const AdvocacyStageRollupProd = lazy(() => import('./pages/Advocacy/StageRollup'));
const AdvocacyExerciseProd = lazy(() => import('./pages/Advocacy/Exercise'));
const AdvocacyCheckInProd = lazy(() => import('./pages/Advocacy/CheckIn'));
const AdvocacySelfCareProd = lazy(() => import('./pages/Advocacy/SelfCare'));
const AdvocacyActionPlanProd = lazy(() => import('./pages/Advocacy/ActionPlan'));
const AdvocacyArtifactProd = lazy(() => import('./pages/Advocacy/Artifact'));
const AdvocacyCarePackProd = lazy(() => import('./pages/Advocacy/CarePack'));
const AdvocacyCrisisProd = lazy(() => import('./pages/Advocacy/Crisis'));
const AdvocacyContributeProd = lazy(() => import('./pages/Advocacy/ContributePage'));
const LandingIntro = lazy(() => import('./components/common/LandingIntro'));
const UnderConstruction = lazy(() => import('./pages/UnderConstruction/UnderConstruction'));
const ChannelsPage = lazy(() => import('./pages/ChannelsPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const FeedPage = lazy(() => import('./pages/FeedPage'));
const SectionDetailPage = lazy(() => import('./pages/SectionDetailPage'));
const ActivityLogPage = lazy(() => import('./pages/ActivityLogPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const EmergencyCardPage = lazy(() => import('./pages/EmergencyCardPage'));
const MoodTrackerPage = lazy(() => import('./pages/MoodTrackerPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));

// Advocacy Preview (sandbox — Figma reference, not integrated)
const AdvocacyPreviewIndex = lazy(() => import('./pages/AdvocacyPreview/PreviewIndex'));
const AdvocacyWorkbookHome = lazy(() => import('./pages/AdvocacyPreview/WorkbookHome'));
const AdvocacyStageDetail = lazy(() => import('./pages/AdvocacyPreview/StageDetail'));
const AdvocacyCheckIn = lazy(() => import('./pages/AdvocacyPreview/CheckIn'));
const AdvocacySelfCare = lazy(() => import('./pages/AdvocacyPreview/SelfCare'));
const AdvocacyExercise = lazy(() => import('./pages/AdvocacyPreview/Exercise'));
const AdvocacyActionPlan = lazy(() => import('./pages/AdvocacyPreview/ActionPlan'));
const AdvocacyArtifact = lazy(() => import('./pages/AdvocacyPreview/Artifact'));
const AdvocacyCrisis = lazy(() => import('./pages/AdvocacyPreview/Crisis'));

// Facilitator Network (post-MVP) — demo-only preview, not integrated
const FacilitatorPreviewPage = lazy(() => import('./pages/FacilitatorPreview/FacilitatorPreviewPage'));

// Wrapper to force QuestionnaireFlow remount when questionId changes
const QuestionnaireFlowWrapper = () => {
  const { questionId } = useParams();
  return <QuestionnaireFlow key={questionId} />;
};

// Check if maintenance mode is enabled (for production "coming soon" page)
const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE?.toLowerCase() === 'true';

// Loading spinner for lazy-loaded pages
// Bloom rests while a page loads; it fades in after a beat so quick loads don't flash.
const PageLoader = () => (
  <div className="page-loader" role="status" aria-label="Loading">
    <Bloom pose="resting" size={72} className="page-loader__bloom" />
  </div>
);

// Inner component that can use auth context
function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  const handleMenuClick = () => {
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  // Initialize analytics if user previously consented
  useEffect(() => { initAnalytics(); }, []);

  // Track page views on route change
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);

  // Session replay only on allow-listed routes (Sentry DPA §2.2.2) — see utils/replayPolicy
  useEffect(() => {
    import('./instrument').then((m) => m.syncReplayForPath?.(location.pathname)).catch(() => {});
  }, [location.pathname]);

  // Listen for custom event from ContentHeader to open sidebar
  React.useEffect(() => {
    const handleOpenSidebar = () => setSidebarOpen(true);
    window.addEventListener('open-sidebar', handleOpenSidebar);
    return () => window.removeEventListener('open-sidebar', handleOpenSidebar);
  }, []);

  // Determine page types
  const authRoutes = ['/login', '/unsubscribe', '/after-a-death', '/register', '/verify-email', '/verification-pending', '/forgot-password', '/reset-password', '/restore-account', '/accept-invitation', '/under-construction', '/onboarding', '/welcome', '/auth/sso-callback', '/care-fund/confirm', '/checkout/mock', '/sponsor/', '/sponsor-a-care-team', '/community', '/payment/complete', '/advocacy-preview', '/facilitator-preview', '/help', '/workbook/team', '/workbook/stage', '/workbook/exercise', '/workbook/checkin', '/workbook/selfcare', '/workbook/actionplan', '/workbook/artifact', '/workbook/crisis'];
  const legalRoutes = ['/legal', '/privacy', '/terms', '/care-team-guidelines', '/cookie-policy', '/recording-consent', '/platform-disclaimer', '/testing-agreement', '/consumer-health-data', '/care-fund-terms', '/advocacy-terms'];
  // The workbook dashboard (/workbook/team/:teamId) and crisis page should show
  // the global sidebar, unlike the immersive exercise/stage pages that share the
  // /workbook/team prefix and stay full-bleed. Re-enable navigation for just
  // these two by exempting them from the authRoutes match.
  const isWorkbookWithSidebar =
    /^\/workbook\/team\/[^/]+\/?$/.test(location.pathname) ||
    location.pathname.startsWith('/workbook/crisis');
  // A team's Remembrance checklist is full screen, like the public page.
  const isImmersive = /^\/team\/[^/]+\/remembrance(\/|$)/.test(location.pathname);
  const isAuthPage =
    isImmersive ||
    (!isWorkbookWithSidebar &&
      (authRoutes.some(route => location.pathname.startsWith(route)) ||
        legalRoutes.includes(location.pathname)));

  // Focus mode = questionnaire pages (hide sidebar, show hamburger)
  const isFocusMode = location.pathname.startsWith('/questionnaire');

  // Show navigation only when logged in and not on auth pages
  const showNavigation = user && !isAuthPage;

  return (
    <div className={`app ${showNavigation && !isFocusMode ? 'app--with-sidebar' : ''}`}>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Desktop sidebar - always visible when logged in (except focus mode) */}
      {showNavigation && !isFocusMode && (
        <Sidebar isMobileOrFocus={false} />
      )}

      {/* Mobile/Focus mode sidebar - slide in/out */}
      {showNavigation && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={handleSidebarClose}
          isMobileOrFocus={true}
        />
      )}

      {/* Top navbar with hamburger - disabled in questionnaire (uses QuestionnaireFlowHeader instead) */}

      {/* Mobile hamburger button */}
      {showNavigation && !isFocusMode && (
        <button
          className="mobile-menu-btn"
          onClick={handleMenuClick}
          aria-label="Open menu"
        >
          <span className="material-icons">menu</span>
        </button>
      )}

      <SeoManager />
      <main className="app__main" id="main-content" tabIndex="-1">
        <EmailVerificationBanner />
        <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/after-a-death" element={<AfterADeath />} />
          <Route path="/verification-pending" element={<VerificationPending />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/restore-account" element={<RestoreAccount />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/auth/sso-callback" element={<SSOCallback />} />
          {/* FR-2A — Care Fund email magic-link confirmation. Public: the
              single-use token in the path is the credential. */}
          <Route path="/care-fund/confirm/:token" element={<CareFundConfirm />} />
          {/* Mock hosted-payment page. Public: the stubbed payment intent's
              redirect_url points here; behaves like an external checkout. */}
          <Route path="/checkout/mock" element={<MockCheckout />} />
          {/* Public sponsor landing: what an invited sponsor sees when a
              family shares their request link. No account needed (NFR-3). */}
          {/* Org self-serve credits (FR-5.3 interim) — secret token from the receipt */}
          <Route path="/sponsor/credits/:token" element={<SponsorCreditsPage />} />
          <Route path="/sponsor/:token" element={<SponsorPage />} />
          {/* Sponsor's own aggregate impact (FR-8.3). Static segment outranks
              the :token route in v6 route ranking. */}
          <Route path="/sponsor/impact" element={<ProtectedRoute><SponsorImpactPage /></ProtectedRoute>} />
          {/* Public Sponsor-a-Care-Team landing (Sponsor PRD v28 showcase) */}
          <Route path="/community" element={<ExternalRedirect to="https://awholefamilymatter.org/community/" />} />
          <Route path="/sponsor-a-care-team" element={<SponsorLandingPage />} />
          <Route path="/sponsor-a-care-team/families" element={<SponsorFamiliesPage />} />
          {/* Bulk-directed org sponsorship — one charge, N families (FR-5.1) */}
          <Route path="/sponsor-a-care-team/organizations" element={<SponsorBulkPage />} />
          {/* Claim a gift addressed to your email (FR-4.1/4.4) */}
          <Route path="/gift" element={<ProtectedRoute><GiftClaimPage /></ProtectedRoute>} />
          <Route path="/gift/:sponsorshipId" element={<ProtectedRoute><GiftClaimPage /></ProtectedRoute>} />
          {/* Hosted-checkout return page. Public: sponsors may have no account. */}
          <Route path="/payment/complete" element={<PaymentComplete />} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/welcome" element={<LandingIntro />} />

          {/* Legal pages */}
          {/* Legal documents — v2.13 texts rendered from /public/legal (single source) */}
          <Route path="/legal" element={<LegalCenter />} />
          <Route path="/privacy" element={<LegalCenter slug="privacy-policy" />} />
          <Route path="/terms" element={<LegalCenter slug="terms-of-service" />} />
          <Route path="/care-team-guidelines" element={<LegalCenter slug="care-team-guidelines" />} />
          <Route path="/cookie-policy" element={<LegalCenter slug="cookie-policy" />} />
          <Route path="/recording-consent" element={<LegalCenter slug="recording-consent" />} />
          <Route path="/platform-disclaimer" element={<LegalCenter slug="platform-disclaimer" />} />
          <Route path="/testing-agreement" element={<LegalCenter slug="testing-agreement" />} />
          <Route path="/consumer-health-data" element={<LegalCenter slug="consumer-health-data" />} />
          <Route path="/care-fund-terms" element={<LegalCenter slug="care-fund-terms" />} />
          <Route path="/advocacy-terms" element={<LegalCenter slug="advocacy-terms" />} />

          {/* Account routes - Protected */}
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/user/:userId" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
          <Route path="/account-settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
          <Route path="/activity-log" element={<ProtectedRoute><ActivityLogPage /></ProtectedRoute>} />
          <Route path="/emergency-card" element={<ProtectedRoute><EmergencyCardPage /></ProtectedRoute>} />
          <Route path="/mood-tracker" element={<ProtectedRoute><MoodTrackerPage /></ProtectedRoute>} />

          {/* Dashboard - Protected */}
          <Route path="/" element={user ? <ProtectedRoute><Dashboard /></ProtectedRoute> : <LandingIntro />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

          {/* Feed - Protected */}
          <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />

          {/* Care Teams - Protected */}
          <Route path="/teams" element={<ProtectedRoute><CareTeamsPage /></ProtectedRoute>} />
          <Route path="/team/:teamId" element={<ProtectedRoute><CareTeamPage /></ProtectedRoute>} />
          <Route path="/team/:teamId/billing" element={<ProtectedRoute><TeamBillingPage /></ProtectedRoute>} />
          <Route path="/team/:teamId/get-sponsored" element={<ProtectedRoute><GetSponsoredPage /></ProtectedRoute>} />
          <Route path="/funding" element={<ProtectedRoute><FundingTeamPicker /></ProtectedRoute>} />
          <Route path="/team/:teamId/care-fund" element={<ProtectedRoute><CareFundLayout /></ProtectedRoute>}>
            <Route index element={<CareFundPage />} />
            <Route path="pool/:poolKey" element={<PoolDetailPage />} />
          </Route>
          <Route path="/team/:teamId/member/:userId" element={<ProtectedRoute><MemberProfilePage /></ProtectedRoute>} />
          <Route path="/team/:teamId/remembrance/:planId" element={<ProtectedRoute><RemembrancePage /></ProtectedRoute>} />
          <Route path="/team/:teamId/channels" element={<ProtectedRoute><ChannelsPage /></ProtectedRoute>} />
          <Route path="/team/:teamId/channels/:channelId" element={<ProtectedRoute><ChannelsPage /></ProtectedRoute>} />

          {/* My Recordings - Protected */}
          <Route path="/my-recordings" element={<ProtectedRoute><MyRecordingsPage /></ProtectedRoute>} />

          {/* Your Progress - Protected */}
          <Route path="/progress" element={<ProtectedRoute><YourProgressPage /></ProtectedRoute>} />
          <Route path="/progress/section/:sectionId" element={<ProtectedRoute><SectionDetailPage /></ProtectedRoute>} />

          {/* Notifications - Protected */}
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

          {/* Help & Support - Protected */}
          <Route path="/help" element={<HelpPage />} />

          {/* Search Results - Protected */}
          <Route path="/search" element={<ProtectedRoute><SearchResultsPage /></ProtectedRoute>} />

          {/* Questionnaire routes - Protected */}
          <Route path="/questionnaire/:questionId" element={<ProtectedRoute><QuestionnaireFlowWrapper /></ProtectedRoute>} />
          <Route path="/questionnaire" element={<ProtectedRoute><Navigate to="/questionnaire/Q10A" replace /></ProtectedRoute>} />

          {/* Under Construction (public) */}
          <Route path="/under-construction" element={<UnderConstruction />} />

          {/* Advocacy Workbook — production, protected. Sandbox at /advocacy-preview/* remains. */}
          {/* Workbook is team-scoped. /workbook (no team) routes to the
              picker, which auto-redirects when the user has a single team. */}
          <Route path="/workbook" element={<ProtectedRoute><AdvocacyWorkbookTeamPickerProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId" element={<ProtectedRoute><AdvocacyWorkbookHomeProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId/stage/:stageKey" element={<ProtectedRoute><AdvocacyStageDetailProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId/stage/:stageKey/summary" element={<ProtectedRoute><AdvocacyStageRollupProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId/exercise/:exerciseId" element={<ProtectedRoute><AdvocacyExerciseProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId/contribute/:exerciseId" element={<ProtectedRoute><AdvocacyContributeProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId/exercise/:exerciseId/checkin" element={<ProtectedRoute><AdvocacyCheckInProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId/exercise/:exerciseId/selfcare" element={<ProtectedRoute><AdvocacySelfCareProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId/exercise/:exerciseId/actionplan" element={<ProtectedRoute><AdvocacyActionPlanProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId/exercise/:exerciseId/artifact" element={<ProtectedRoute><AdvocacyArtifactProd /></ProtectedRoute>} />
          <Route path="/workbook/team/:teamId/care-pack" element={<ProtectedRoute><AdvocacyCarePackProd /></ProtectedRoute>} />
          <Route path="/workbook/crisis" element={<ProtectedRoute><AdvocacyCrisisProd /></ProtectedRoute>} />

          {/* Back-compat: old /workbook/exercise/X URLs route to the picker
              so users with bookmarks land on a sensible page. */}
          <Route path="/workbook/exercise/*" element={<Navigate to="/workbook" replace />} />
          <Route path="/workbook/stage/*" element={<Navigate to="/workbook" replace />} />

          {/* Advocacy Preview — sandbox / Figma reference, public, no auth */}
          <Route path="/facilitator-preview" element={<FacilitatorPreviewPage />} />
          <Route path="/advocacy-preview" element={<AdvocacyPreviewIndex />} />
          <Route path="/advocacy-preview/workbook" element={<AdvocacyWorkbookHome />} />
          <Route path="/advocacy-preview/stage/:stageKey" element={<AdvocacyStageDetail />} />
          <Route path="/advocacy-preview/session/checkin" element={<AdvocacyCheckIn />} />
          <Route path="/advocacy-preview/session/selfcare" element={<AdvocacySelfCare />} />
          <Route path="/advocacy-preview/session/exercise" element={<AdvocacyExercise />} />
          <Route path="/advocacy-preview/session/actionplan" element={<AdvocacyActionPlan />} />
          <Route path="/advocacy-preview/artifact" element={<AdvocacyArtifact />} />
          <Route path="/advocacy-preview/crisis" element={<AdvocacyCrisis />} />

          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <RouteAnnouncer />
      {showNavigation && <ScrollToTop />}
      <PlanUnlockedModal />
      <CookieConsentBanner />
    </div>
  );
}

// The community landing moved to the marketing site; keep old links working.
function ExternalRedirect({ to }) {
  React.useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

function App() {
  // If maintenance mode is enabled, show only the Under Construction page
  if (isMaintenanceMode) {
    return (
      <ThemeProvider>
        <Suspense fallback={<PageLoader />}>
          <UnderConstruction />
        </Suspense>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SubscriptionProvider>
            <NotificationProvider>
              <QuestionnaireProvider>
                <Router>
                  <AppContent />
                </Router>
              </QuestionnaireProvider>
            </NotificationProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
